import { computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { environment } from '@dxp/core/environment';
import {
    assignKpiTeamThresholdFromLegacy,
    IKpiTeamThreshold,
    IRbacTeam,
    ITeam,
    ITelXLKpiTeamThreshold,
    Team,
    TelXLKpiTeamThresholdDefaultState,
} from '@dxp/core/models';
import {
    signalStore,
    withComputed,
    withFeature,
    withHooks,
    withMethods,
    withState,
} from '@ngrx/signals';
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    from,
    map,
    mergeMap,
    of,
    switchMap,
    tap,
    toArray,
} from 'rxjs';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { RbacApiService } from '@dxp/core/api';
import { ReloadService } from '@dxp/core/services';
import { withAgents } from '../agent/agent.feature';
import { AGENT_STORE } from '../agent/agent.token';
import { withQueues } from '../queue/queue.feature';
import { QUEUE_STORE } from '../queue/queue.token';
import { areStringArraysEqualUnordered } from './team.helpers';

const KPI_CONCURRENCY = 5;

interface ITeamStoreState {
    teamIds: Record<string, IRbacTeam>;
    kpi: Record<string, ITelXLKpiTeamThreshold>;
    loading: boolean;
}

const initialState: ITeamStoreState = {
    teamIds: {},
    kpi: {},
    loading: true,
};

export const TeamStore = signalStore(
    { providedIn: 'root' },
    environment.storeWithDevTools('TeamStore'),
    withFeature(() => withAgents(inject(AGENT_STORE))),
    withFeature(() => withQueues(inject(QUEUE_STORE))),
    withState<ITeamStoreState>(initialState),
    withComputed(store => ({
        ready: computed<boolean>(() => !store.loading()),
        teams: computed<ITeam[]>(() => {
            const teams = Object.values(store.teamIds());
            const thresholds = store.kpi();

            return teams.map(team => {
                const agents = store
                    .agents()
                    .filter(a => team.memberIds.includes(a.id));
                const queues = store
                    .queues()
                    .filter(q => team.queueIds.includes(q.id));
                const threshold = thresholds[team.id];

                return new Team(team.id, team.name, agents, queues, threshold);
            });
        }),
    })),
    withMethods(store => ({
        getTeamKpi(teamId: string): ITelXLKpiTeamThreshold {
            return store.kpi()[teamId] ?? TelXLKpiTeamThresholdDefaultState;
        },
        updateAgentsFromTeams(teamIds: IRbacTeam[]) {
            teamIds.forEach(teamId => {
                updateState(store, 'UPDATE_TEAM_ARRAY', state => {
                    const prev = state.teamIds[teamId.id];

                    if (!prev) {
                        return {
                            ...state,
                            teamIds: {
                                ...state.teamIds,
                                [teamId.id]: teamId,
                            },
                        };
                    }

                    if (
                        teamId.name === prev.name &&
                        areStringArraysEqualUnordered(
                            prev.memberIds,
                            teamId.memberIds,
                        ) &&
                        areStringArraysEqualUnordered(
                            prev.queueIds,
                            teamId.queueIds,
                        )
                    ) {
                        return state;
                    }

                    return {
                        ...state,
                        teamIds: {
                            ...state.teamIds,
                            [teamId.id]: teamId,
                        },
                    };
                });
            });
        },
        updateTeamKpi(
            teamId: string,
            kpiThreshold?: IKpiTeamThreshold,
            telXLKpiThreshold?: ITelXLKpiTeamThreshold,
        ) {
            const kpi =
                telXLKpiThreshold ??
                assignKpiTeamThresholdFromLegacy(kpiThreshold);

            updateState(store, 'UPDATE_TEAM_KPI', state => {
                return {
                    ...state,
                    kpi: {
                        ...state.kpi,
                        [teamId]: kpi,
                    },
                    loading: false,
                };
            });
        },
        removeTeam(teamId: string) {
            updateState(store, 'REMOVE_TEAM', state => {
                const { [teamId]: _, ...newKpi } = state.kpi;
                const { [teamId]: __, ...newTeam } = state.teamIds;
                return {
                    ...state,
                    teamIds: newTeam,
                    kpi: newKpi,
                };
            });
        },
    })),
    withHooks({
        onInit(
            store,
            oidcSecurityService = inject(OidcSecurityService),
            rbacApiService = inject(RbacApiService),
            reloadService = inject(ReloadService),
            destroyRef = inject(DestroyRef),
        ) {
            combineLatest(
                oidcSecurityService.userData$,
                toObservable(reloadService.refreshTime),
                toObservable(reloadService.refreshTeams),
            )
                .pipe(
                    map(([user, refresh, refreshTeams]) => ({
                        userId: user?.userData?.sub as unknown,
                        refresh,
                        refreshTeams,
                    })),
                    filter(
                        (
                            x,
                        ): x is {
                            userId: string;
                            refresh: number;
                            refreshTeams: number;
                        } =>
                            typeof x.userId === 'string' && x.userId.length > 0,
                    ),
                    distinctUntilChanged(
                        (a, b) =>
                            a.userId === b.userId &&
                            a.refresh === b.refresh &&
                            a.refreshTeams === b.refreshTeams,
                    ),
                    switchMap(({ userId }) =>
                        rbacApiService.getTeams(userId).pipe(
                            catchError(error => {
                                reloadService.error(
                                    'TEAM_STORE',
                                    error.message,
                                );

                                return of([]);
                            }),
                        ),
                    ),
                    map(teams =>
                        teams.filter(
                            t =>
                                t.memberIds.length > 0 && t.queueIds.length > 0,
                        ),
                    ),
                    tap(teams => store.updateAgentsFromTeams(teams)),
                    map(teams => teams.map(t => t.id)),
                    distinctUntilChanged(areStringArraysEqualUnordered),
                    switchMap(teamIds => {
                        if (teamIds.length === 0) {
                            reloadService.error('TEAM_STORE', 'No teams found');

                            return of(
                                [] as { teamId: string; kpis?: undefined }[],
                            );
                        }

                        return from(teamIds).pipe(
                            mergeMap(
                                teamId =>
                                    rbacApiService
                                        .getKpiThresholds(teamId, 'teams')
                                        .pipe(
                                            catchError(() => of(undefined)),
                                            map(kpis => ({ teamId, kpis })),
                                        ),
                                KPI_CONCURRENCY,
                            ),
                            toArray(),
                        );
                    }),
                    tap(
                        (
                            results: {
                                teamId: string;
                                kpis:
                                    | {
                                          kpiThreshold: IKpiTeamThreshold;
                                          kpiTelXLThreshold: ITelXLKpiTeamThreshold;
                                      }
                                    | undefined
                                    | null;
                            }[],
                        ) => {
                            for (const { teamId, kpis } of results) {
                                store.updateTeamKpi(
                                    teamId,
                                    kpis?.kpiThreshold,
                                    kpis?.kpiTelXLThreshold,
                                );
                            }

                            const resultTeamIds = new Set(
                                results.map(t => t.teamId),
                            );
                            const existingTeamIds = Object.keys(
                                store.teamIds(),
                            );

                            const removedTeamIds = existingTeamIds.filter(
                                teamId => !resultTeamIds.has(teamId),
                            );

                            for (const teamId of removedTeamIds) {
                                store.removeTeam(teamId);
                            }
                        },
                    ),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();
        },
    }),
);
